import { useState } from "react";
import { ArrowRight, Check, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { ROOMS, WHATSAPP } from "../data/rooms";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const inputCls = "bg-white border border-stone-200 rounded-full px-5 py-3 text-sm text-[#2E2A26] placeholder:text-[#2E2A26]/30 focus:outline-none focus:border-[#C85A38]";

const WaitlistSignup = () => {
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [targetDates, setTargetDates] = useState("");
  const [roomPreference, setRoomPreference] = useState("");
  const [stayLength, setStayLength] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!firstName.trim() || !email.trim()) return;
    const cleanedPhone = phone.trim().replace(/[\s\-().]/g, "");
    if (cleanedPhone && !/^\+\d{6,15}$/.test(cleanedPhone)) {
      toast.error("Please include your country code (e.g. +34 612 345 678)");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/waitlist/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          first_name: firstName.trim(),
          email: email.trim(),
          phone: phone.trim(),
          target_dates: targetDates.trim(),
          room_preference: roomPreference,
          stay_length: stayLength
        })
      });
      if (!res.ok) throw new Error("submit failed");
      if (typeof window !== "undefined" && window.gtag) {
        window.gtag("event", "waitlist_signup", { event_category: "conversion" });
      }
      setDone(true);
    } catch {
      toast.error("That didn't send — please try again, or message Steve on WhatsApp.");
    }
    setSubmitting(false);
  };

  const whatsappHref = () => {
    const bits = [
      `Hi Steve! I'm ${firstName.trim() || "interested in Savi"}`,
      targetDates.trim() ? `looking at ${targetDates.trim()}` : "",
      stayLength ? `for ${stayLength}` : "",
      roomPreference && roomPreference !== "No preference" ? `(${roomPreference})` : "",
    ].filter(Boolean).join(" ");
    return `${WHATSAPP}?text=${encodeURIComponent(`${bits}. Just sent my dates through the site — could we chat?`)}`;
  };

  if (done) {
    return (
      <div className="bg-[#FBF7F1] rounded-3xl p-8 sm:p-12 border border-[#C85A38]/20 text-center" data-testid="waitlist-success">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#C85A38]/10 mb-5">
          <Check className="w-7 h-7 text-[#C85A38]" />
        </div>
        <h3 className="font-serif text-2xl sm:text-3xl text-[#2E2A26] mb-3">
          Got it, {firstName.split(" ")[0]} — Steve&apos;s checking your dates.
        </h3>
        <p className="text-[#2E2A26]/60 text-sm sm:text-base max-w-md mx-auto leading-relaxed mb-6">
          He&apos;ll reply personally within 24 hours with what&apos;s free for your window — or the closest alternative. Keep an eye on your inbox, and WhatsApp if you left a number.
        </p>
        <a
          href={whatsappHref()}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1fb958] text-white rounded-full px-7 py-3 text-sm font-medium transition-colors"
          data-testid="waitlist-whatsapp-cta"
        >
          <MessageCircle className="w-4 h-4" /> Prefer to chat? Message Steve now
        </a>
      </div>
    );
  }

  return (
    <div className="bg-[#FBF7F1] rounded-3xl p-6 sm:p-10 border border-stone-200" data-testid="waitlist-form">
      <div className="text-center mb-7">
        <p className="text-[#C85A38] text-xs font-medium tracking-[0.2em] uppercase mb-3">Check dates &amp; rooms</p>
        <h3 className="font-serif text-2xl sm:text-3xl text-[#2E2A26] mb-3">
          Tell us when, and roughly how long.
        </h3>
        <p className="text-[#2E2A26]/60 text-sm sm:text-base max-w-xl mx-auto leading-relaxed">
          Steve reads every one of these himself and comes back within 24 hours with what&apos;s free — or the nearest thing to it. No sales sequence, no pressure.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="max-w-xl mx-auto space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <input type="text" autoComplete="given-name" required value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="First name" className={inputCls} data-testid="waitlist-first-name" />
          <input type="email" autoComplete="email" inputMode="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com" className={inputCls} data-testid="waitlist-email" />
        </div>
        <input type="tel" autoComplete="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="WhatsApp / phone (with country code) — optional" className={`w-full ${inputCls}`} data-testid="waitlist-phone" />
        <input type="text" value={targetDates} onChange={(e) => setTargetDates(e.target.value)} placeholder="Preferred arrival (e.g. early November, or 1 Nov – 30 Nov)" className={`w-full ${inputCls}`} data-testid="waitlist-dates" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <select value={stayLength} onChange={(e) => setStayLength(e.target.value)} className={inputCls} data-testid="waitlist-stay-length">
            <option value="">How long?</option>
            <option value="1 month">1 month</option>
            <option value="2 months">2 months</option>
            <option value="3 months">3 months</option>
            <option value="3+ months">3+ months</option>
          </select>
          <select value={roomPreference} onChange={(e) => setRoomPreference(e.target.value)} className={inputCls} data-testid="waitlist-room-preference">
            <option value="">Which room?</option>
            <option value="No preference">No preference — whatever&apos;s free</option>
            {ROOMS.map((r) => (
              <option key={r.id} value={r.shortName}>{r.shortName} · {r.available}</option>
            ))}
          </select>
        </div>

        <button
          type="submit"
          disabled={submitting || !firstName || !email}
          className="w-full bg-[#C85A38] hover:bg-[#A84830] disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium tracking-wide rounded-full px-6 py-3.5 transition-colors flex items-center justify-center gap-2 mt-2"
          data-testid="waitlist-submit"
        >
          {submitting ? "Sending..." : "Check my dates"}
          {!submitting && <ArrowRight className="w-4 h-4" />}
        </button>

        <div className="grid grid-cols-3 gap-2 pt-4 text-center" data-testid="waitlist-next-steps">
          {[
            ["1", "Steve replies within 24h with what's free"],
            ["2", "You confirm; a 30% deposit holds the room"],
            ["3", "Balance 30 days before arrival, then the app takes over"],
          ].map(([n, t]) => (
            <div key={n} className="px-1">
              <span className="inline-flex w-6 h-6 rounded-full bg-[#C85A38]/10 text-[#C85A38] text-xs font-semibold items-center justify-center mb-1.5">{n}</span>
              <p className="text-[#2E2A26]/55 text-[11px] leading-snug">{t}</p>
            </div>
          ))}
        </div>
      </form>
    </div>
  );
};

export default WaitlistSignup;
